import {Button, Checkbox, Divider, Form, Input, withForm, WithFormProps} from '@innowise-group/ui-kit';
import {ChangeEvent, useState} from 'react';
import {Controller, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {RoomType} from '@innowise-group/core';
import {RoomTypesSelect} from '../room-type-select';
import {TypeRoomEnum} from '@shared/utilities/determine-room-meeting-id.utility';
import {Observable} from 'rxjs';
import {isPlainObject} from 'lodash';

export enum EditRoomFormFields {
  IsOur = 'isOur',
  RoomNumber = 'number',
  RoomType = 'type',
  MeetingRoomId = 'meetingRoomId',
}

const ERROR_TEXT = 'No MeetingRoomCalendar matches the given query.';

export type EditRoomValues = {
  isOur: boolean;
  number: string;
  type: RoomType;
  meetingRoomId: string | null;
};

const EditRoomForm: React.FC<
  Omit<WithFormProps<EditRoomValues>, 'onSubmit'> & {
    onSubmit: (values: EditRoomValues) => Observable<unknown>;
    onDelete?: () => Observable<unknown>;
  }
> = ({onSubmit, onCancel, onDelete, initialValues}) => {
  const {isOur, number, type, meetingRoomId} = initialValues;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const {
    handleSubmit,
    control,
    formState: {errors, isDirty, isValid},
    setError,
    setValue,
    watch,
  } = useFormContext();

  const {t} = useTranslation();
  const isMeetingIdFieldShown = watch(EditRoomFormFields.RoomType)
    ? watch(EditRoomFormFields.RoomType) === TypeRoomEnum.Meeting
    : type === TypeRoomEnum.Meeting;

  const handleSubmitForm = (values: EditRoomValues) => {
    setIsSubmitting(true);
    onSubmit(values).subscribe({
      next: () => {
        setIsSubmitting(false);
        onCancel();
      },
      error: (error) => {
        const message = String(error?.message || error || '');
        if (message.includes(ERROR_TEXT) || (isPlainObject(error) && String(error).includes(ERROR_TEXT))) {
          setError(EditRoomFormFields.MeetingRoomId, {
            type: 'custom',
            message: t('modals.messages.meetingIdErrorMsg'),
          });
        }
        setIsSubmitting(false);
      },
    });
  };

  const handleDelete = () => {
    if (!onDelete || isSubmitting || isDeleting) {
      return;
    }
    setIsDeleting(true);
    onDelete().subscribe({
      next: () => onCancel(),
      error: () => setIsDeleting(false),
      complete: () => setIsDeleting(false),
    });
  };

  const isBusy = isSubmitting || isDeleting;

  return (
    <Form.Container onSubmit={handleSubmit(handleSubmitForm)}>
      <Form.Layout layoutType="vertical">
        <Controller
          defaultValue={isOur}
          name={EditRoomFormFields.IsOur}
          control={control}
          render={({field: {onChange, value}}) => (
            <Form.Field error={errors[EditRoomFormFields.IsOur]}>
              <Checkbox
                checked={value}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const newValue = e.target.checked;
                  onChange(newValue);
                  if (newValue === false && watch(EditRoomFormFields.MeetingRoomId)) {
                    setValue(EditRoomFormFields.MeetingRoomId, null, {shouldValidate: true});
                  }
                }}
                position="left"
              >
                {t('modals.isOur')}
              </Checkbox>
            </Form.Field>
          )}
        />

        <Divider />

        <Controller
          defaultValue={number}
          name={EditRoomFormFields.RoomNumber}
          control={control}
          render={({field: {onChange, value}}) => (
            <Form.Field error={errors[EditRoomFormFields.RoomNumber]} label={t('modals.roomName')}>
              <Input defaultValue={value} onChange={onChange} />
            </Form.Field>
          )}
        />

        <Divider />

        {watch(EditRoomFormFields.IsOur, isOur) ? (
          <>
            <Controller
              defaultValue={type}
              name={EditRoomFormFields.RoomType}
              control={control}
              render={({field: {onChange, value}}) => (
                <Form.Field error={errors[EditRoomFormFields.RoomType]} label={t('modals.roomType')}>
                  <RoomTypesSelect value={value} onValueChange={onChange} />
                </Form.Field>
              )}
            />
            {isMeetingIdFieldShown && (
              <Controller
                defaultValue={meetingRoomId}
                name={EditRoomFormFields.MeetingRoomId}
                control={control}
                render={({field: {onChange, value}}) => {
                  return (
                    <Form.Field
                      error={errors[EditRoomFormFields.MeetingRoomId]?.message}
                      label={t('modals.meetingRoomId')}
                    >
                      <Input defaultValue={value} onChange={onChange} />
                    </Form.Field>
                  );
                }}
              />
            )}
          </>
        ) : null}
      </Form.Layout>
      <Form.Actions>
        <Button type="submit" disabled={isBusy || !isValid || (isValid && !isDirty)} withLoader={isSubmitting}>
          {t('buttons.save')}
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={isBusy}>
          {t('buttons.cancel')}
        </Button>
        {onDelete ? (
          <Button type="button" variant="outlined" onClick={handleDelete} disabled={isBusy} withLoader={isDeleting}>
            {t('buttons.delete')}
          </Button>
        ) : null}
      </Form.Actions>
    </Form.Container>
  );
};

export default withForm(EditRoomForm);
